import React, { useState, useEffect } from 'react';
import { Icon } from './Icon';
import type { CardQR } from '../types/person';
import QrButton from './QrButton';

interface QRAccessModalProps {
  isVisible: boolean;
  personId: string;
  personName: string;
  employeeNo: string;
  onClose: () => void;
  onGenerateQR: (employeeNo: string, fecha_visita: string) => Promise<{ success: boolean, message?: string }>;
  onDownloadQR: (accessId: string) => Promise<{ success: boolean, message?: string }>;
  onLoadAccesses: (personId: string) => Promise<{ success: boolean, data?: CardQR[] }>;
  onDeleteQR: (qrId: number) => Promise<{ success: boolean, message?: string }>;
  generating: boolean;
  deleting: boolean;
}

export const QRAccessModal: React.FC<QRAccessModalProps> = ({
  isVisible,
  personId,
  personName,
  employeeNo,
  onClose,
  onGenerateQR,
  // onDownloadQR,
  onLoadAccesses,
  onDeleteQR,
  generating,
  deleting
}) => {
  const [accesses, setAccesses] = useState<CardQR[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDateForm, setShowDateForm] = useState(false);
  const [validationDateTime, setValidationDateTime] = useState('');
  // const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible && personId) {
      loadAccesses();
    }
  }, [isVisible, personId]);

  const loadAccesses = async () => {
    setAccesses([]);

    setLoading(true);
    try {
      const accessList = await onLoadAccesses(personId);
      if (accessList.success && accessList.data) {
        setAccesses(accessList.data);
      }
      //   setAccesses(accessList);
    } catch (error) {
      console.error('Error loading accesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    if (!validationDateTime) {
      alert('Por favor selecciona una fecha y hora válida');
      return;
    }

    const formattedDateTime = validationDateTime.replace('T', ' ') + ':00';

    try {
      const result = await onGenerateQR(formattedDateTime, employeeNo);
      if (result.success) {
        setShowDateForm(false);
        setValidationDateTime('');
        await loadAccesses(); // Recargar la lista
      } else {
        alert(result.message || 'Error al generar el QR');
      }
    } catch (error) {
      console.error('Error generating QR:', error);
      alert('Error al generar el QR');
    }
  };


  const handleDeleteQR = async (qrId: number) => {
    if (window.confirm(`¿Estás seguro de eliminar este registro QR?`)) {
      try {
        await onDeleteQR(qrId)
        alert(`QR eliminado`);
        loadAccesses();
      } catch (error) {

        alert(`Error al eliminar el empleado: ${error}`);
      }
    }
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-EC', {
      timeZone: 'America/Guayaquil',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTodayDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}`;
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Accesos QR
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {personName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-black hover:bg-gray-100 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors"
          >
            <Icon name="close" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Generate QR Button */}
          <div className="mb-6 flex items-center justify-between">
            <button
              onClick={() => setShowDateForm(true)}
              disabled={generating}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="plus" className="w-4 h-4 mr-2" />
              Generar Acceso QR
            </button>

            <button
              onClick={loadAccesses}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm bg-gray-50 dark:bg-gray-600 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Icon name={loading ? "loading" : "refresh"} className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>

          {/* Date Form */}
          {showDateForm && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha y hora de ingreso
                  </label>
                  <input
                    type="datetime-local"
                    value={validationDateTime}
                    onChange={(e) => setValidationDateTime(e.target.value)}
                    min={getTodayDateTime()}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleGenerateQR}
                    disabled={generating || !validationDateTime}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {generating ? (
                      <Icon name="loading" className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Icon name="check" className="w-4 h-4 mr-2" />
                    )}
                    Crear
                  </button>
                  <button
                    onClick={() => {
                      setShowDateForm(false);
                      setValidationDateTime('');
                    }}
                    className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <Icon name="x" className="w-4 h-4 mr-2" />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Accesses List */}
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 dark:border-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fecha de Visita
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fecha de Creación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-gray-600 dark:text-gray-300">Cargando accesos...</span>
                      </div>
                    </td>
                  </tr>
                ) : accesses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <Icon name="list" className="w-12 h-12 text-gray-400" />
                        <span className="text-gray-500 dark:text-gray-400 text-lg">
                          No hay accesos QR generados
                        </span>
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          Genera un nuevo acceso QR para comenzar
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  accesses.map((access) => (
                    <tr
                      key={access.personas_qr_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                    >

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDateTime(access.fecha_visita)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDateTime(access.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${access.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}
                        >
                          {access.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="flex flex-row px-2 py-4  text-center">
                        <QrButton
                          number={access.numero_tarjeta}
                          fecha_visita={access.fecha_visita}
                          name={personName ? personName : "N/A"}
                          variant="outline"
                          size="lg"
                          className="ml-4"
                        />
                        <button
                          onClick={() => handleDeleteQR(access.personas_qr_id)}
                          disabled={deleting}
                          className="flex items-center justify-center mx-auto px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {/* {downloading === access.personas_qr_id ? (
                            <Icon name="loading" className="w-4 h-4 animate-spin" />
                          ) : (
                            <Icon name="download" className="w-4 h-4 mr-1" />
                          )} */}
                          {/* <Icon name="trash" className="w-4 h-4 mr-1" /> */}
                          <Icon name={deleting ? "loading" : "trash"} className={`w-4 h-4 mr-1 ${deleting ? 'animate-spin' : ''}`} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};